import {
  computeFilteredPapers,
  computeOrderedPaperList,
} from '../compute/widget-state';
import { CorpusId } from '../models/aliases';
import { DrawerContainer } from './DrawerContainer';
import { events } from '../analytics/events';
import { ExternalTrack } from '../analytics/common';
import {
  FacetName,
  Order,
  PaperFilter,
  WidgetClientState,
} from '../models/widget-state';
import { FacetsPanel } from './FacetsPanel';
import { Notify } from '../models/notifications';
import { NotifyContextProvider } from '../hooks/useNotify';
import { PaperComponent } from './paper/PaperComponent';
import { paperFinderTheme } from '../styled/custom-theme';
import { PaperPagination } from './PapersPagination';
import { PapersResult } from './PapersResult';
import { Reaction } from '../models/reactions';
import { SelectedActionBar } from './SelectedActionsBar';
import { TopBar } from './TopBar';
import {
  TrackingContextProvider,
  useTrackedHandler,
} from '../hooks/useTracking';
import { useExportDialog } from '../hooks/useExportDialog';
import { useReactionsData } from '../hooks/useReactionsData';
import { WidgetActionsDispatcher } from '../models/widget-actions';

import { getTheme, VarnishApp } from '@allenai/varnish2';
import { Theme, useMediaQuery } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import React, { useCallback, useEffect, useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';

export interface PaperTitleOverrideProps {
  corpusId: number;
  title: string;
}

interface PapersPanelProps {
  baseUrl: string;
  widgetState: WidgetClientState;
  dispatchAction: WidgetActionsDispatcher;
  showCloseWidget: boolean;
  AdditionalControlsComponent?: PaperComponent;
  PaperTitleOverride?: React.FC<PaperTitleOverrideProps>;
  isReadOnly?: boolean;
  notify?: Notify;
  track?: ExternalTrack;
  onReactionChanged?: (
    corpusId: CorpusId,
    reaction: Reaction | undefined,
  ) => void;
  onTextFeedbackSubmitted?: (corpusId: CorpusId, text: string) => void;
}

export function PapersPanel(props: PapersPanelProps): JSX.Element {
  const { notify = () => {}, track = () => {} } = props;

  const theme = getTheme(paperFinderTheme);

  return (
    <ThemeProvider theme={theme}>
      <VarnishApp theme={theme}>
        <NotifyContextProvider value={notify}>
          <TrackingContextProvider
            value={(eventBuilder, payload) => {
              const event = eventBuilder(payload);
              track(event.name, event.payload ?? {});
            }}
          >
            <PapersPanelContent {...props} />
          </TrackingContextProvider>
        </NotifyContextProvider>
      </VarnishApp>
    </ThemeProvider>
  );
}

export function PapersPanelContent(props: PapersPanelProps): JSX.Element {
  const {
    baseUrl,
    widgetState,
    dispatchAction,
    AdditionalControlsComponent,
    PaperTitleOverride,
    isReadOnly = false,
    notify = () => {},
    onReactionChanged = () => {},
    onTextFeedbackSubmitted = () => {},
  } = props;
  const isSmallScreen = useMediaQuery(
    (theme: Theme) => theme.breakpoints.down('md'),
    {
      noSsr: true,
    },
  );
  const { DownloadDialog, openDownloadDialog } = useExportDialog();
  const [isFacetsDrawerOpen, setFacetsDrawerOpen] = useState(false);

  const {
    papers: remoteSortedPapers,
    ordering: remoteOrdering,
    originalOrderedPapers,
    selectedPapers,
    numPages,
    indexOfFirstPaperInPage: remoteIndexOfFirstPaperInPage,
    indexOfLastPaperInPage: remoteIndexOfLastPaperInPage,
    currentPage: remoteCurrentPage,
    syncStatus,
    isLocked,
    nextStateId,
    prevStateId,
    filters: remoteFilters,
    widgetId,
  } = widgetState;

  const { reactions, updateReaction, submitTextFeedback } = useReactionsData(
    baseUrl,
    widgetId,
  );

  const handleReactionChanged = useCallback(
    async (
      corpusId: CorpusId,
      reaction: Reaction | undefined,
    ): Promise<void> => {
      try {
        await updateReaction({ corpusId, reaction });
        onReactionChanged(corpusId, reaction);
      } catch (error) {
        notify('Error saving reaction, please try again', 'error');
      }
    },
    [updateReaction],
  );

  const handleTextFeedbackSubmitted = useCallback(
    async (corpusId: CorpusId, text: string): Promise<void> => {
      try {
        await submitTextFeedback(corpusId, text);
        notify('Thanks for your feedback!', 'success');
        onTextFeedbackSubmitted(corpusId, text);
      } catch (error) {
        notify('Error saving feedback, please try again', 'error');
      }
    },
    [submitTextFeedback],
  );

  // local state, is use in read only mode instead of the remote one
  const [localCurrentPage, setLocalCurrentPage] = useState<number>(0);
  const [localOrdering, setLocalOrdering] = useState<Order | null>(null);
  const [localFilters, setLocalFilters] = useState<Array<PaperFilter>>([]);

  const handlePaperSelectionChanged = useTrackedHandler(
    events.selection.PaperSelectionChanged,
    (corpusId: CorpusId, isSelect: boolean): void => {
      dispatchAction({
        kind: isSelect ? 'select-paper' : 'unselect-paper',
        corpusId,
      });
    },
    (corpusId, isSelected) => ({ corpusId, isSelected }),
  );

  // sync the local state, when the "owner" of the thread
  // changes the state that results in another list of papers
  // either by starting a new search or by removing papers from the
  // current result set (widget state)
  useEffect(() => {
    setLocalCurrentPage(widgetState.currentPage);
    setLocalOrdering(widgetState.ordering);
    setLocalFilters(widgetState.filters ?? []);
  }, [isReadOnly, widgetState.widgetId, widgetState.papers.length]);

  const ordering = isReadOnly ? localOrdering : remoteOrdering;
  const currentPage = isReadOnly ? localCurrentPage : remoteCurrentPage;
  const filters = isReadOnly ? localFilters : remoteFilters;

  const localIndexOfFirstPaperInPage = localCurrentPage * widgetState.pageSize;
  const localIndexOfLastPaperInPage =
    (localCurrentPage + 1) * widgetState.pageSize - 1;

  const indexOfFirstPaperInPage = isReadOnly
    ? localIndexOfFirstPaperInPage
    : remoteIndexOfFirstPaperInPage;
  const indexOfLastPaperInPage = isReadOnly
    ? localIndexOfLastPaperInPage
    : remoteIndexOfLastPaperInPage;

  let papers = remoteSortedPapers;
  if (isReadOnly) {
    const filteredPapers = computeFilteredPapers(
      originalOrderedPapers,
      filters ?? [],
    );
    const orderedPapers = computeOrderedPaperList(filteredPapers, ordering);
    papers = orderedPapers;
  }

  const filteredPapersByPage = papers.slice(
    Math.max(indexOfFirstPaperInPage, 0),
    Math.min(indexOfLastPaperInPage + 1, papers.length),
  );

  const handlePageChange = useTrackedHandler(
    events.pagination.PageSelectionChanged,
    (currentPage: number): void => {
      if (isReadOnly) {
        setLocalCurrentPage(currentPage);
      } else {
        dispatchAction({ kind: 'update-current-page', currentPage });
      }
    },
    (page) => ({ page }),
  );

  const handleOrderChanged = (order: Order | null): void => {
    if (isReadOnly) {
      setLocalOrdering(order);
      setLocalCurrentPage(0);
    } else {
      if (order !== null) {
        dispatchAction({
          kind: 'sort-papers',
          ...order,
        });
      } else {
        dispatchAction({ kind: 'clear-sort' });
      }
    }
  };

  const handleFacetSelected = useTrackedHandler(
    events.facets.FacetSelectionChanged,
    (facetName: FacetName, value: string): void => {
      if (isReadOnly) {
        setLocalFilters((filters) => [
          ...filters,
          { categoryName: facetName, value },
        ]);
        setLocalCurrentPage(0);
      } else {
        dispatchAction({ kind: 'add-filter', categoryName: facetName, value });
      }
    },
    (facetName, value) => ({ facetName, value, isSelected: true }),
  );

  const handleFacetUnSelected = useTrackedHandler(
    events.facets.FacetSelectionChanged,
    (facetName: FacetName, value: string): void => {
      if (isReadOnly) {
        setLocalFilters((filters) =>
          filters.filter(
            (f) => !(f.categoryName === facetName && f.value === value),
          ),
        );
        setLocalCurrentPage(0);
      } else {
        dispatchAction({
          kind: 'remove-filter',
          categoryName: facetName,
          value,
        });
      }
    },
    (facetName, value) => ({ facetName, value, isSelected: false }),
  );

  const handleClearAllFilters = useTrackedHandler(
    events.facets.ClearAllClicked,
    (): void => {
      if (isReadOnly) {
        setLocalFilters([]);
        setLocalCurrentPage(0);
      } else {
        dispatchAction({ kind: 'clear-all-filters' });
      }
    },
  );

  const handleClearAllSelectedPapers = useTrackedHandler(
    events.selection.ClearAllClicked,
    (): void => {
      if (!isReadOnly) {
        dispatchAction({ kind: 'clear-all-selected-papers' });
      }
    },
  );

  const handleUndo = useTrackedHandler(events.history.UndoClicked, (): void => {
    dispatchAction({ kind: 'undo' });
  });

  const handleRedo = useTrackedHandler(events.history.RedoClicked, (): void => {
    dispatchAction({ kind: 'redo' });
  });

  const handleExportAll = useTrackedHandler(
    events.export.ExportFilteredClicked,
    (): void => {
      openDownloadDialog({
        type: 'export-all',
        query: widgetState.query,
        papers,
      });
    },
  );

  const handleExportSelected = useTrackedHandler(
    events.export.ExportSelectedClicked,
    (): void => {
      const selectedPapersSet = new Set(selectedPapers);
      openDownloadDialog({
        type: 'export-selected',
        query: widgetState.query,
        papers: papers.filter((p) => selectedPapersSet.has(p.corpusId)),
      });
    },
  );

  const handleOpenFacetsDrawer = useTrackedHandler(
    events.panels.ShowMobileFacetPanelChanged,
    (): void => {
      setFacetsDrawerOpen(true);
    },
    () => ({ isExpanded: true }),
  );

  const handleCloseFacetsDrawer = useTrackedHandler(
    events.panels.ShowMobileFacetPanelChanged,
    (): void => {
      setFacetsDrawerOpen(false);
    },
    () => ({ isExpanded: false }),
  );

  return (
    <MainLayout>
      <TopBar
        isLocked={isLocked}
        syncStatus={syncStatus}
        filtersCount={filters.length}
        numTotalPapers={originalOrderedPapers.length}
        numFilteredPapers={papers.length}
        undoDisabled={isReadOnly || prevStateId === undefined}
        onUndoClicked={handleUndo}
        redoDisabled={isReadOnly || nextStateId === undefined}
        onRedoClicked={handleRedo}
        onExportAllClicked={handleExportAll}
        onOpenFacetsDrawerClicked={handleOpenFacetsDrawer}
      />
      <SelectedActionBar
        selectedPapers={selectedPapers}
        isReadOnly={isReadOnly}
        onClearAllSelectedClicked={handleClearAllSelectedPapers}
        onExportSelectedClicked={handleExportSelected}
      />
      {isLocked && <Loader color="inherit" variant="indeterminate" />}
      {!isLocked && originalOrderedPapers.length === 0 && (
        <NoResultDiv key="open">
          <WarningSpan>No Papers Found.</WarningSpan>
        </NoResultDiv>
      )}
      {originalOrderedPapers.length > 0 && (
        <>
          <PapersDiv id="papers-content">
            <PapersAndFooter>
              <PapersResult
                isReadOnly={isReadOnly}
                papersData={filteredPapersByPage}
                selectedPapers={selectedPapers}
                onPaperSelectionChange={handlePaperSelectionChanged}
                AdditionalControlsComponent={AdditionalControlsComponent}
                PaperTitleOverride={PaperTitleOverride}
                reactions={reactions}
                onReactionChanged={handleReactionChanged}
                onTextFeedbackSubmitted={handleTextFeedbackSubmitted}
              />
              <Footer>
                {papers.length > 0 && (
                  <PaperPagination
                    pages={numPages}
                    currentPage={currentPage}
                    onCurrentPageChange={handlePageChange}
                  />
                )}
              </Footer>
            </PapersAndFooter>
            <DrawerContainer
              isDrawerActive={isSmallScreen}
              isDrawerOpen={isFacetsDrawerOpen}
              onCloseDrawer={handleCloseFacetsDrawer}
              zindex={1210}
            >
              <FacetsPanel
                papers={originalOrderedPapers}
                filters={filters}
                onSelectedFilter={handleFacetSelected}
                onUnSelectedFilter={handleFacetUnSelected}
                ordering={ordering}
                onOrderChanged={handleOrderChanged}
                onClearAllFiltersClicked={handleClearAllFilters}
              />
            </DrawerContainer>
          </PapersDiv>
        </>
      )}
      <DownloadDialog />
    </MainLayout>
  );
}

const PapersDiv = styled.div`
  display: flex;
  width: 100%;
  overflow: hidden;
  flex: 1;
  justify-content: flex-end;
  position: relative;
`;

const NoResultDiv = styled.div`
  padding: 10px;
  width: 100%;
  max-height: 500px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const Loader = styled(LinearProgress)`
  width: 100%;
  color: ${({ theme }) => `${theme.color.loader.bar}`};
`;

const MainLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  place-items: center;
  height: 100%;
  width: 100%;
  position: relative;
  background-color: ${({ theme }) => theme.color.background.primary};
  border-radius: 4px;
  color: ${({ theme }) => theme.color.text.primary};
`;

const PapersAndFooter = styled.div`
  flex: 1;
  height: 100%;
  min-width: 400px;
  display: flex;
  flex-direction: column;
`;

const Footer = styled.div`
  display: flex;
  justify-content: start;
  width: 100%;
  align-items: center;
  height: 50px;
  padding: 3px 10px;
`;

const WarningSpan = styled.span`
  font-size: ${({ theme }) => theme.font.size.large};
  font-weight: normal;
  user-select: none;
  cursor: default;
`;
